import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import { Button } from '@/Components/ui/button';
import { Menu, X, Phone, User, LogOut } from 'lucide-react';
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/Components/ui/hover-card";

export default function Header() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userName, setUserName] = useState('');

    useEffect(() => {
        const checkAuth = async () => {
            const user = await base44.auth.me();
            setIsAuthenticated(!!user);
            if (user) {
                setUserName(user.full_name || user.email?.split('@')[0]);
            }
        };
        checkAuth();

        const subscription = base44.auth.onAuthStateChange(async (event, session) => {
            const isAuth = !!session;
            setIsAuthenticated(isAuth);
            if (isAuth) {
                const user = await base44.auth.me();
                setUserName(user?.full_name || user?.email?.split('@')[0]);
            } else {
                setUserName('');
            }
        });

        return () => {
            if (subscription && subscription.unsubscribe) {
                subscription.unsubscribe();
            }
        };
    }, []);

    const navItems = [
        { label: 'Home', href: 'Home' },
        { label: 'Riparazioni', href: 'Riparazioni' },
        { label: 'Manutenzione', href: 'Manutenzione' },
        { label: 'Chi Siamo', href: 'ChiSiamo' },
        { label: 'FAQ', href: 'FAQ' },
        { label: 'Contatti', href: 'Contatti' },
    ];

    const handleLogout = async () => {
        await base44.auth.logout();
        // No need to reload or redirect manually if onAuthStateChange handles state, 
        // but logout often clears session and might want redirect to home
        window.location.href = '/login';
    };

    return (
        <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b">
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to={createPageUrl('Home')} className="flex items-center">
                        <img
                            src="/images/tecnosys-logo.png"
                            alt="Tecnosys Logo"
                            className="h-10"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-8">
                        {navItems.map((item) => (
                            <Link
                                key={item.href}
                                to={createPageUrl(item.href)}
                                className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    {/* Right side actions */}
                    <div className="flex items-center gap-4">
                        <div className="hidden lg:flex items-center gap-3">
                            <a href="tel:+393296139684" className="flex items-center gap-2 text-gray-700 text-sm">
                                <Phone className="w-4 h-4" />
                                329 6139684
                            </a>
                            {isAuthenticated ? (
                                <HoverCard openDelay={200} closeDelay={200}>
                                    <HoverCardTrigger asChild>
                                        <Link to={createPageUrl('AreaClienti')}>
                                            <Button variant="ghost" className="text-gray-700 gap-2">
                                                <User className="w-4 h-4" />
                                                Area Clienti
                                            </Button>
                                        </Link>
                                    </HoverCardTrigger>
                                    <HoverCardContent className="w-48 bg-white p-2" align="end">
                                        <div className="flex flex-col gap-2">
                                            <p className="text-xs font-medium text-gray-500 px-2 py-1">Ciao, {userName || 'Utente'}!</p>
                                            <Link to={createPageUrl('AreaClienti')} className="w-full">
                                                <Button variant="ghost" size="sm" className="w-full justify-start">
                                                    Dashboard
                                                </Button>
                                            </Link>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleLogout}
                                                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <LogOut className="w-4 h-4 mr-2" />
                                                Logout
                                            </Button>
                                        </div>
                                    </HoverCardContent>
                                </HoverCard>
                            ) : (
                                <Link to={createPageUrl('Login')}>
                                    <Button variant="ghost" className="text-gray-700 gap-2">
                                        <User className="w-4 h-4" />
                                        Login
                                    </Button>
                                </Link>
                            )}
                            <Link to="/riparazioni#segnala-guasto">
                                <Button className="rounded-full bg-blue-600 hover:bg-blue-700 text-white">
                                    Richiedi Assistenza
                                </Button>
                            </Link>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-md"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="lg:hidden pb-4 border-t mt-2 max-h-[calc(100vh-4rem)] overflow-y-auto">
                        <div className="flex flex-col gap-2 pt-4">
                            {navItems.map((item) => (
                                <Link
                                    key={item.href}
                                    to={createPageUrl(item.href)}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md"
                                >
                                    {item.label}
                                </Link>
                            ))}

                            {isAuthenticated ? (
                                <>
                                    <Link to={createPageUrl('AreaClienti')} onClick={() => setMobileMenuOpen(false)}>
                                        <Button variant="ghost" className="w-full mt-2 border border-black rounded-full gap-2">
                                            <User className="w-4 h-4" />
                                            Area Clienti
                                        </Button>
                                    </Link>
                                    <Button
                                        variant="ghost"
                                        className="w-full mt-2 text-red-500 hover:text-red-700 hover:bg-red-50 gap-2"
                                        onClick={async () => {
                                            await base44.auth.logout();
                                            window.location.href = '/login';
                                        }}
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Logout
                                    </Button>
                                </>
                            ) : (
                                <Link to={createPageUrl('Login')} onClick={() => setMobileMenuOpen(false)}>
                                    <Button variant="ghost" className="w-full mt-2 border border-black rounded-full gap-2">
                                        <User className="w-4 h-4" />
                                        Login
                                    </Button>
                                </Link>
                            )}

                            <Link to="/riparazioni#segnala-guasto">
                                <Button className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white">
                                    Richiedi Assistenza
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </nav>
        </header >
    );
}